import { zodResolver } from "@hookform/resolvers/zod";
import { designTokens, RiCircleFill } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FloatingMenu } from "@itandi/itandi-bb-ui/FloatingMenu";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { Heading } from "@itandi/itandi-bb-ui/Heading";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { PDFViewer } from "@itandi/itandi-bb-ui/PDFViewer";
import { YokuaruBody } from "@itandi/itandi-bb-ui/YokuaruBody";
import { YokuaruFooter } from "@itandi/itandi-bb-ui/YokuaruFooter";
import { YokuaruHeader } from "@itandi/itandi-bb-ui/YokuaruHeader";
import { useRouter } from "next/router";
import { useState } from "react";
import {
	FormProvider,
	useFieldArray,
	useForm,
	useFormContext,
	useWatch
} from "react-hook-form";
import useSWRMutation from "swr/mutation";
import { BackToEditConfirmModal } from "~/components/specifics/deals/[id]/estimations/ocr_requests/[requestId]/BackToEditConfirmModal";
import { EstimationOcrItemForm } from "~/components/specifics/deals/[id]/estimations/ocr_requests/[requestId]/EstimationOcrItemForm";
import { useDynamicHeight } from "~/hooks/useDynamicHeight";
import type { NskResponse } from "~/models/Api";
import type {
	DealEstimationOcrFormInput,
	DealEstimationOcrFormValues
} from "~/models/DealEstimationOcrForm";
import {
	buildDealEstimationOcrFormValues,
	buildEmptyDealEstimationOcrFormItem,
	calculateDealEstimationOcrAmounts,
	dealEstimationOcrFormSchema
} from "~/models/DealEstimationOcrForm";
import type { DealEstimationOcrRequest } from "~/models/DealEstimationOcrRequest";
import { isSuccess } from "~/utils/api/api_handler";
import {
	getFilePath,
	reflect
} from "~/utils/api/deals/DealEstimationOcrRequest";
import { toThousandSeparator } from "~/utils/CurrencySeparator";
import { generatePath } from "~/utils/generatePath";

type Props = {
	backToEditPath: string;
	items: NonNullable<DealEstimationOcrRequest["items"]>;
	ocrRequest: Omit<DealEstimationOcrRequest, "items">;
};

const PDF_PANE_WIDTH = "45%";
const SUMMARY_MARKER_SIZE = "0.75rem";

function EstimationOcrAmountsSummary() {
	const { control } = useFormContext<DealEstimationOcrFormInput>();
	const items = useWatch({ control, name: "items" });
	const amounts = calculateDealEstimationOcrAmounts(items);

	return (
		<Flex alignItems="center" gap={designTokens["spacing/m"]}>
			<Grid
				alignItems="center"
				columnGap={designTokens["spacing/xxs"]}
				gridTemplateColumns="auto auto auto"
				rowGap={designTokens["spacing/xs"]}
				width="fit-content"
			>
				<RiCircleFill
					color={designTokens["color/chips/presentation/foreground/red"]}
					size={SUMMARY_MARKER_SIZE}
				/>
				<Label size="s">提案項目を除く小計: </Label>
				<Flex justifyContent="flex-end">
					<Label size="s" weight="bold">
						{`¥${toThousandSeparator(amounts.nonProposalTotalAmount) ?? "0"}`}
					</Label>
				</Flex>
				<RiCircleFill
					color={designTokens["color/chips/presentation/foreground/blue"]}
					size={SUMMARY_MARKER_SIZE}
				/>
				<Label size="s">提案項目小計: </Label>
				<Flex justifyContent="flex-end">
					<Label size="s" weight="bold">
						{`¥${toThousandSeparator(amounts.proposalTotalAmount) ?? "0"}`}
					</Label>
				</Flex>
			</Grid>
			<Flex direction="column" flexShrink={0} gap={designTokens["spacing/xxs"]}>
				<Label size="s">税抜き合計金額</Label>
				<Label size="xl" weight="bold">
					{`¥${toThousandSeparator(amounts.totalAmount) ?? "0"}`}
				</Label>
			</Flex>
		</Flex>
	);
}

export function SucceededState({ backToEditPath, items, ocrRequest }: Props) {
	const router = useRouter();
	const { notify } = useNotification();
	const filePath = getFilePath(ocrRequest.dealId, ocrRequest.id);
	const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen] = useState(false);
	const { height: columnsHeight, ref: columnsRef } =
		useDynamicHeight<HTMLDivElement>();
	const { height: panelHeight, ref: panelRef } =
		useDynamicHeight<HTMLDivElement>();
	const methods = useForm<DealEstimationOcrFormInput>({
		defaultValues: buildDealEstimationOcrFormValues(items),
		mode: "onChange",
		resolver: zodResolver(dealEstimationOcrFormSchema)
	});
	const { fields, append, remove } = useFieldArray({
		control: methods.control,
		name: "items"
	});
	const { trigger, isMutating } = useSWRMutation<
		NskResponse,
		never,
		string,
		DealEstimationOcrFormValues
	>(
		generatePath(
			"/deals/:deal_id/deal_estimation_ocr_requests/:id/reflection",
			{ deal_id: ocrRequest.dealId, id: ocrRequest.id }
		),
		async (_key, { arg }) =>
			await reflect(ocrRequest.dealId, ocrRequest.id, arg)
	);
	const handleReflect = methods.handleSubmit(async (values) => {
		const response = await trigger(values as DealEstimationOcrFormValues);
		if (isSuccess(response)) {
			notify({
				content: "見積明細を反映しました。",
				variant: "success"
			});
			await router.push(backToEditPath);
		} else {
			notify({
				content:
					response.message ??
					"エラーが発生しました。時間をおいて再度お試しください。",
				variant: "error"
			});
		}
	});
	return (
		<FormProvider {...methods}>
			<Grid
				gridTemplateColumns={`${PDF_PANE_WIDTH} 1fr`}
				height={columnsHeight}
				ref={columnsRef}
				width="100%"
			>
				<PDFViewer
					height="100%"
					src={filePath}
					title="見積書原本PDF"
					width="100%"
				/>
				<div>
					<YokuaruHeader
						heading={<Heading as="h1">読み取り結果の確認・修正</Heading>}
						sticky
					/>
					<Grid height={panelHeight} overflowY="auto" ref={panelRef}>
						<YokuaruBody>
							<Panel>
								<PanelBody>
									<Grid gap={designTokens["spacing/m"]}>
										<EstimationOcrAmountsSummary />
										<Alertv2 variant="info">
											<Paragraph>
												AIによる読み取り結果には誤りが含まれる場合があります。
												<br />
												原本PDFをご確認の上、必要に応じて修正してください。
											</Paragraph>
										</Alertv2>
										{fields.map((field, index) => (
											<EstimationOcrItemForm
												disabled={isMutating}
												index={index}
												key={field.id}
												onRemove={remove}
											/>
										))}
										<Flex justifyContent="flex-end">
											<Buttonv2
												disabled={isMutating}
												onClick={() => {
													append(buildEmptyDealEstimationOcrFormItem());
												}}
												variant="secondary"
											>
												明細を追加
											</Buttonv2>
										</Flex>
									</Grid>
								</PanelBody>
							</Panel>
						</YokuaruBody>
					</Grid>
					<YokuaruFooter
						floatingMenu={
							<FloatingMenu
								fitToParent
								rightArea={
									<Flex alignItems="center" gap={designTokens["spacing/s"]}>
										<Buttonv2
											disabled={isMutating}
											onClick={() => {
												setIsLeaveConfirmModalOpen(true);
											}}
											variant="secondary"
										>
											見積作成画面へもどる
										</Buttonv2>
										<Buttonv2
											disabled={isMutating || fields.length === 0}
											loading={isMutating}
											onClick={() => {
												void handleReflect();
											}}
											variant="primary"
										>
											見積に反映
										</Buttonv2>
									</Flex>
								}
							/>
						}
					/>
				</div>
				<BackToEditConfirmModal
					alertMessage={
						<>
							編集中の内容は保存されません。
							<br />
							今回の読み取り結果にもどることはできません。
						</>
					}
					backToEditPath={backToEditPath}
					isOpen={isLeaveConfirmModalOpen}
					onClose={() => {
						setIsLeaveConfirmModalOpen(false);
					}}
				/>
			</Grid>
		</FormProvider>
	);
}
